/**
 * GENERATED modules of a composed game: an accepted composed spec carries the
 * exact checked records of the generated modules its structure uses
 * (`ComposedGameSpecV1.modules`), so the authority is self-contained — its
 * digest covers the module code, and every reader derives the game's library
 * from the spec alone (the maintained catalog plus those records).
 *
 * Running a record needs a host: `node:vm` behind
 * `@djgg/gameplay-kit/compose/generated/host`, which this package never
 * imports (it is bundled into the portal and into sandboxes that have no
 * Node built-ins). A Node process that reads composed games registers that
 * host once at startup with {@link setComposedModuleHostV1}; a spec with no
 * generated modules never touches it, and one with them fails closed in a
 * process without a host.
 */
import { MAINTAINED_LIBRARY, extendLibrary, type AnyComposeModule, type ComposeLibraryV1, type TemplateV1 } from '@djgg/gameplay-kit/compose';
import type { GeneratedModuleRecordV1 } from '@djgg/gameplay-kit/compose/generated/host';

export type GeneratedComposeModuleV1 = GeneratedModuleRecordV1;

/** Instantiates a stored generated-module record (verifying its digest and
 * identity) — `instantiateGeneratedModuleRecord`. */
export interface ComposedModuleHostV1 {
  instantiate(record: GeneratedComposeModuleV1): AnyComposeModule;
}

/** Generated modules one game may carry (the plan's ceiling). */
export const COMPOSED_GENERATED_MODULES_MAX = 4;
const SOURCE_MAX_BYTES = 16 * 1024;
const JS_MAX_BYTES = 64 * 1024;
const MODULE_ID = /^[a-z][a-zA-Z0-9]*\.[a-z][a-zA-Z0-9]*$/;

let host: ComposedModuleHostV1 | null = null;

/** Register this process's generated-module host (once, at startup). */
export function setComposedModuleHostV1(next: ComposedModuleHostV1): void {
  host = next;
}

/** Instantiated records by identity, kept with the exact bytes the host
 * verified: a later record reuses an instance only when its bytes are the
 * same (a record claiming a digest its source is not goes back to the host,
 * which refuses it). */
const instances = new Map<string, { record: GeneratedComposeModuleV1; module: AnyComposeModule }>();

const sameRecord = (a: GeneratedComposeModuleV1, b: GeneratedComposeModuleV1) =>
  a.id === b.id && a.version === b.version && a.digest === b.digest && a.source === b.source && a.js === b.js;

/** The library a composed game runs on: the maintained catalog plus its
 * generated modules and, for a game composed whole (the plan's generate
 * level), its generated template — the maintained catalog itself when it has
 * neither. Records are immutable, so each is instantiated once per process.
 * The template is taken as given: its validation is its carrier's. */
export function composedLibraryV1(modules: readonly GeneratedComposeModuleV1[], generatedTemplate: TemplateV1 | null): ComposeLibraryV1 {
  const templates = generatedTemplate ? [generatedTemplate] : [];
  if (!modules.length) return templates.length ? extendLibrary(MAINTAINED_LIBRARY, { templates }) : MAINTAINED_LIBRARY;
  if (!host) throw new Error('This process has no generated-module host (setComposedModuleHostV1); it cannot run a game with generated modules');
  const loaded = modules.map(m => {
    const at = `${m.id}@${m.version}:${m.digest}`;
    const cached = instances.get(at);
    if (cached && sameRecord(cached.record, m)) return cached.module;
    const module = host!.instantiate(m);
    instances.set(at, { record: { ...m }, module });
    return module;
  });
  return extendLibrary(MAINTAINED_LIBRARY, { modules: loaded, templates });
}

const utf8Bytes = (s: string) => new TextEncoder().encode(s).length;

/** Validate the untrusted `modules` of a composed spec: shapes and bounds
 * only (a host verifies each record's digest and identity when it runs it;
 * the spec's derivation proves the game uses every one). */
export function validateGeneratedComposeModulesV1(value: unknown): GeneratedComposeModuleV1[] {
  if (!Array.isArray(value) || value.length < 1 || value.length > COMPOSED_GENERATED_MODULES_MAX) {
    throw new Error(`Composed generated modules: expected 1..${COMPOSED_GENERATED_MODULES_MAX} records`);
  }
  const ids = new Set<string>();
  return value.map((raw, i) => {
    const at = `Composed generated module ${i}`;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw) || ![Object.prototype, null].includes(Object.getPrototypeOf(raw))
      || Object.keys(raw).sort().join(',') !== 'digest,id,js,source,version') throw new Error(`${at}: expected { id, version, digest, source, js }`);
    const r = raw as Record<string, unknown>;
    if (typeof r.id !== 'string' || !MODULE_ID.test(r.id) || MAINTAINED_LIBRARY.modules.has(r.id) || ids.has(r.id)) {
      throw new Error(`${at}: expected a new module id (not a maintained one, not repeated)`);
    }
    ids.add(r.id);
    if (!Number.isInteger(r.version) || (r.version as number) < 1) throw new Error(`${at}: expected a positive integer version`);
    if (typeof r.digest !== 'string' || !/^[0-9a-f]{64}$/.test(r.digest)) throw new Error(`${at}: expected a sha-256 digest`);
    if (typeof r.source !== 'string' || utf8Bytes(r.source) > SOURCE_MAX_BYTES) throw new Error(`${at}: expected source of at most ${SOURCE_MAX_BYTES} bytes`);
    if (typeof r.js !== 'string' || utf8Bytes(r.js) > JS_MAX_BYTES) throw new Error(`${at}: expected js of at most ${JS_MAX_BYTES} bytes`);
    return { id: r.id, version: r.version as number, digest: r.digest, source: r.source, js: r.js };
  });
}
